import {
  ChecklistFieldDef,
  ChecklistRepeatableGroupDef,
  ChecklistSectionDef,
  fieldHasConfiguredFollowUps,
  normalizeDeedIdForSelect,
  repeatableEntriesKey,
} from "../components/DynamicChecklistManagement/DynamicChecklistManagement";
import { matchNaturePropertyKey, surveyConfiguredNatureCards } from "./dynamicChecklistSurveyNature";
import { normalizeMappedDropdownValue } from "./dynamicChecklistDropdownValue";

/** Hierarchy levels matching General Detail: Schedule → Survey → Boundary → Measurement. */
export type SurveyHierarchyLevel = "schedule" | "survey" | "boundary" | "measurement";

export const SURVEY_HIERARCHY_LEVELS: { key: SurveyHierarchyLevel; label: string }[] = [
  { key: "schedule", label: "Schedule" },
  { key: "survey", label: "Survey" },
  { key: "boundary", label: "Boundary" },
  { key: "measurement", label: "Measurement" },
];

export const SURVEY_HIERARCHY_META = {
  FD_ID: "__sv_fd_id__",
  SCHEDULE_ID: "__sv_schedule_id__",
  SUR_ID: "__sv_sur_id__",
  BOUNDARY_ID: "__sv_boundary_id__",
  MEASUREMENT_ID: "__sv_measurement_id__",
  NATURE_KEY: "__sv_nature_key__",
  SCHEDULE_POS: "__sv_schedule_pos__",
  DOC_NO: "__sv_doc_no__",
  /** TS No + "Old Survey No" variant (General Details `ts_old_survey_no`). */
  TS_OLD_SURVEY_NO: "__sv_ts_old_survey_no__",
  /** TS No + "Old TS No" variant (General Details `ts_old_ts_no`). */
  TS_OLD_TS_NO: "__sv_ts_old_ts_no__",
} as const;

/** Legacy section-wide hierarchy group id (pre–nature nesting). */
export function legacySurveyHierarchyGroupId(level: SurveyHierarchyLevel): string {
  return `rg_survey_h_${level}`;
}

/** Hierarchy group id scoped to a nature-of-property key. */
export function surveyHierarchyGroupId(
  naturePropertyKey: string,
  level: SurveyHierarchyLevel
): string {
  return `rg_survey_h_${naturePropertyKey}_${level}`;
}

export function surveyHierarchyLinkedGroups(
  section: ChecklistSectionDef,
  naturePropertyKey?: string | null
): ChecklistRepeatableGroupDef[] {
  const groups = (section.repeatableGroups || []).filter((g) => g.surveyHierarchyLevel != null);
  const natureKey = String(naturePropertyKey ?? "").trim();
  if (!natureKey) return groups;
  const hasAnyScoped = groups.some((g) => g.naturePropertyKey != null);
  if (!hasAnyScoped) return groups;
  return groups.filter((g) => g.naturePropertyKey === natureKey);
}

export function surveyHierarchyFieldCount(
  section: ChecklistSectionDef,
  naturePropertyKey?: string | null
): number {
  return surveyHierarchyLinkedGroups(section, naturePropertyKey).reduce(
    (n, g) => n + g.fields.length,
    0
  );
}

export function surveyHierarchyGroupHasChecklistFields(
  group: ChecklistRepeatableGroupDef | undefined | null
): boolean {
  return !!group && (group.fields?.length ?? 0) > 0;
}

/** True when the section uses Schedule → Survey → Boundary → Measurement hierarchy. */
export function surveySectionUsesHierarchy(section: ChecklistSectionDef): boolean {
  const groups = surveyHierarchyLinkedGroups(section);
  if (!groups.length) return false;
  if (groups.some((g) => (g.fields?.length ?? 0) > 0)) return true;
  return surveyConfiguredNatureCards(section).length > 0;
}

export function surveyHierarchyGroupForLevel(
  section: ChecklistSectionDef,
  level: SurveyHierarchyLevel,
  naturePropertyKey?: string | null
): ChecklistRepeatableGroupDef | undefined {
  const natureKey = String(naturePropertyKey ?? "").trim();
  if (natureKey) {
    const scoped = surveyHierarchyLinkedGroups(section, natureKey).find(
      (g) => g.surveyHierarchyLevel === level && g.naturePropertyKey === natureKey
    );
    if (scoped) return scoped;
  }
  return surveyHierarchyLinkedGroups(section).find(
    (g) => g.surveyHierarchyLevel === level && g.naturePropertyKey == null
  );
}

export function surveyHierarchyLevelsForNature(
  section: ChecklistSectionDef,
  naturePropertyKey: string
): SurveyHierarchyLevel[] {
  return surveyHierarchyLinkedGroups(section, naturePropertyKey)
    .map((g) => g.surveyHierarchyLevel)
    .filter((l): l is SurveyHierarchyLevel => l != null);
}

/** Dynamic field value tags → API row keys per hierarchy level. */
export const SURVEY_HIERARCHY_TAG_TO_ROW_KEY: Record<string, string> = {
  gdSchedule_nature_doc: "nature_doc",
  gdSchedule_door_no: "door_no",
  gdSchedule_flat_no: "flat_no",
  gdSchedule_floor: "floor",
  gdSchedule_appt_name: "appt_name",
  gdSchedule_buildup: "buildup",
  gdSchedule_uds: "uds",
  gdSchedule_address: "address",
  gdSchedule_remark: "remark",
  gdSchedule_total_extent: "total_extent",
  gdSchedule_extent_unit: "extent_unit",
  gdSchedule_applicant_name: "applicant_name",
  gdSchedule_schedule_id: "schedule_id",
  gdSchedule_uds_unit: "uds_unit",
  gdSchedule_builtup_unit: "builtup_unit",
  gdSchedule_co_applicant_name: "co_applicant_name",
  gdSchedule_scheduleasper: "scheduleasper",
  gdSchedule_asPerPatta: "asPerPatta",

  gdSurvey_survey_no: "survey_no",
  gdSurvey_old_survey_no: "old_survey_no",
  gdSurvey_survey_type: "survey_type",
  gdSurvey_revenue_record: "revenue_record",
  gdSurvey_extent: "extent",
  gdSurvey_sur_extent_unit: "sur_extent_unit",
  gdSurvey_plot_no: "plot_no",
  gdSurvey_layout_name: "layout_name",
  gdSurvey_ts_street_name: "ts_street_name",
  gdSurvey_ts_ward: "ts_ward",
  gdSurvey_ts_block: "ts_block",
  gdSurvey_old_ts_no: "old_ts_no",
  gdSurvey_old_ts_s_no: "old_ts_s_no",
  gdSurvey_ts_no: "ts_no",
  gdSurvey_natham_survey_no: "natham_survey_no",
  gdSurvey_old_natham_survey_no: "old_natham_survey_no",
  gdSurvey_unit: "unit",

  gdBoundary_north: "north",
  gdBoundary_south: "south",
  gdBoundary_east: "east",
  gdBoundary_west: "west",
  gdBoundary_land_aries: "land_aries",
  gdBoundary_direction: "direction",
  gdBoundary_unit: "unit",
  gdBoundary_measurement: "measurement",
  gdBoundary_plot_item_no: "plot_item_no",
  gdBoundary_boundaryNoType: "boundaryNoType",
  gdBoundary_boundary_as_per: "boundary_as_per",
  gdBoundary_asperVAO: "asperVAO",
  gdBoundary_asperVR: "asperVR",
  gdBoundary_asperTS: "asperTS",
  gdBoundary_asperRP: "asperRP",

  gdMeasure_measurement: "measurement",
  gdMeasure_m_unit: "m_unit",
  gdMeasure_l_unit: "l_unit",
  gdMeasure_plot_item_no: "plot_item_no",
  gdMeasure_measureNoType: "measureNoType",
  gdMeasDir_label_name: "label_name",
  gdMeasDir_value: "value",
  gdMeasDir_measure_custom: "measure_custom",
};

/** Legacy Survey Verification / Title Deed tags → General Detail row keys (same as gd* tags). */
export const SURVEY_LEGACY_TAG_TO_ROW_KEY: Record<string, string> = {
  surveyNo: "survey_no",
  oldSurveyNo: "old_survey_no",
  pattaExtractSno: "survey_no",
  extent: "extent",
  deedExtentverfy: "extent",
  deedDoorNoverfy: "door_no",
  deedflatnoverfy: "flat_no",
  floor: "floor",
  deedapartnameverfy: "appt_name",
  deedplotNoverfy: "plot_no",
  deedlayoutnameverfy: "layout_name",
  deedstreetnameverfy: "ts_street_name",
  boundverfyNorth: "north",
  boundverfySouth: "south",
  boundverfyEast: "east",
  boundverfyWest: "west",
  measurementextentmatch: "measurement",
  name: "applicant_name",
};

/** Resolve any survey dynamic tag (gd* or legacy title-deed tag) to a General Detail row key. */
export function resolveSurveyTagRowKey(tag: string): string {
  const t = normalizeSurveyTagId(tag);
  if (!t) return "";
  return SURVEY_HIERARCHY_TAG_TO_ROW_KEY[t] ?? SURVEY_LEGACY_TAG_TO_ROW_KEY[t] ?? "";
}

/** True when a mapped tag can write back via survey hierarchy row updates. */
export function isSurveyHierarchyWriteTag(tag: string): boolean {
  const t = normalizeSurveyTagId(tag);
  if (!t) return false;
  if (t.startsWith(MEASUREMENT_DIRECTION_TAG_PREFIX)) return true;
  return resolveSurveyTagRowKey(t) !== "";
}

/**
 * Legacy SURVEY_VERIFICATION_DYNAMIC_TAGS duplicated by gd* hierarchy tags.
 * Omitted from the Dynamic Field Value dropdown; gd* tags are canonical.
 */
export const SURVEY_VERIFICATION_TAGS_OVERLAPPING_GD = new Set([
  "surveyNo",
  "oldSurveyNo",
  "pattaExtractSno",
  "extent",
  "deedExtentverfy",
  "deedDoorNoverfy",
  "deedflatnoverfy",
  "floor",
  "deedapartnameverfy",
  "deedplotNoverfy",
  "deedlayoutnameverfy",
  "deedstreetnameverfy",
  "boundverfyNorth",
  "boundverfySouth",
  "boundverfyEast",
  "boundverfyWest",
  "measurementextentmatch",
  "name",
]);

/** Flat-only SURVEY_VERIFICATION_DYNAMIC_TAGS → DB write targets (not covered by gd* hierarchy rows). */
export const SURVEY_VERIFICATION_FLAT_TAG_TARGET: Record<
  string,
  { target: "extrafield" | "general_details" | "file_doc" | "doc_scurty"; column: string }
> = {
  pattaissuedverfy: { target: "doc_scurty", column: "issued_by" },
  Corelation: { target: "file_doc", column: "current_survey" },
  proceedingpatta: { target: "file_doc", column: "current_survey" },
  tslrNo: { target: "extrafield", column: "tslrNo" },
  aRegisterMeanscompare: { target: "extrafield", column: "aRegisterMeanscompare" },
};

/** Legacy flat tags — backward compat for existing checklist configs (use gd* for new mappings). */
export const SURVEY_VERIFICATION_LEGACY_FLAT_TAG_TARGET: Record<
  string,
  { target: "extrafield" | "general_details" | "file_doc"; column: string }
> = {
  surveyNo: { target: "file_doc", column: "current_survey" },
  oldSurveyNo: { target: "file_doc", column: "old_survey" },
  pattaExtractSno: { target: "file_doc", column: "current_survey" },
  boundverfyNorth: { target: "extrafield", column: "boundverfyNorth" },
  boundverfySouth: { target: "extrafield", column: "boundverfySouth" },
  boundverfyEast: { target: "extrafield", column: "boundverfyEast" },
  boundverfyWest: { target: "extrafield", column: "boundverfyWest" },
  measurementextentmatch: { target: "extrafield", column: "measurementextentmatch" },
  deedExtentverfy: { target: "extrafield", column: "deedExtentverfy" },
  deedDoorNoverfy: { target: "general_details", column: "door_no" },
  deedflatnoverfy: { target: "general_details", column: "flat_no" },
  floor: { target: "general_details", column: "floor" },
  deedapartnameverfy: { target: "general_details", column: "appt_name" },
  deedplotNoverfy: { target: "extrafield", column: "deedplotNoverfy" },
  deedlayoutnameverfy: { target: "extrafield", column: "deedlayoutnameverfy" },
  deedstreetnameverfy: { target: "extrafield", column: "deedstreetnameverfy" },
  name: { target: "general_details", column: "applicant_name" },
};

export const SURVEY_FLAT_TAG_TARGET: Record<
  string,
  { target: "extrafield" | "general_details" | "file_doc" | "doc_scurty"; column: string }
> = {
  ...SURVEY_VERIFICATION_LEGACY_FLAT_TAG_TARGET,
  ...SURVEY_VERIFICATION_FLAT_TAG_TARGET,
};

export function isSurveyFlatWriteTag(tag: string): boolean {
  const t = String(tag ?? "").trim();
  return t !== "" && t in SURVEY_FLAT_TAG_TARGET;
}

const SURVEY_HIERARCHY_DYNAMIC_TAG_LABELS: Record<string, string> = {
  gdSchedule_nature_doc: "gdSchedule_nature_doc — Schedule Nature of property",
  gdSchedule_door_no: "gdSchedule_door_no — Schedule Door No",
  gdSchedule_flat_no: "gdSchedule_flat_no — Schedule Flat No",
  gdSchedule_floor: "gdSchedule_floor — Schedule Floor",
  gdSchedule_appt_name: "gdSchedule_appt_name — Schedule Apartment Name",
  gdSchedule_buildup: "gdSchedule_buildup — Schedule Built-up",
  gdSchedule_uds: "gdSchedule_uds — Schedule UDS",
  gdSchedule_address: "gdSchedule_address — Schedule Address",
  gdSchedule_remark: "gdSchedule_remark — Schedule Remark",
  gdSchedule_total_extent: "gdSchedule_total_extent — Schedule Total Extent",
  gdSchedule_extent_unit: "gdSchedule_extent_unit — Schedule Extent Unit",
  gdSchedule_applicant_name: "gdSchedule_applicant_name — Schedule Applicant",
  gdSchedule_schedule_id: "gdSchedule_schedule_id — Schedule No (pos)",
  gdSchedule_uds_unit: "gdSchedule_uds_unit — Schedule UDS Unit",
  gdSchedule_builtup_unit: "gdSchedule_builtup_unit — Schedule Built-up Unit",
  gdSchedule_co_applicant_name: "gdSchedule_co_applicant_name — Schedule Co-applicant",
  gdSchedule_scheduleasper: "gdSchedule_scheduleasper — Schedule As Per (stored value)",
  gdSchedule_asPerPatta: "gdSchedule_asPerPatta — As Per Patta Extract",

  gdSurvey_survey_no: "gdSurvey_survey_no — Survey No",
  gdSurvey_old_survey_no: "gdSurvey_old_survey_no — Survey Old Survey No",
  gdSurvey_survey_type: "gdSurvey_survey_type — Survey Type",
  gdSurvey_revenue_record: "gdSurvey_revenue_record — Survey Revenue Record",
  gdSurvey_extent: "gdSurvey_extent — Survey Extent",
  gdSurvey_sur_extent_unit: "gdSurvey_sur_extent_unit — Survey Unit",
  gdSurvey_plot_no: "gdSurvey_plot_no — Survey Plot No",
  gdSurvey_layout_name: "gdSurvey_layout_name — Survey Layout Name",
  gdSurvey_ts_street_name: "gdSurvey_ts_street_name — Survey Street Name",
  gdSurvey_ts_ward: "gdSurvey_ts_ward — Survey Ward",
  gdSurvey_ts_block: "gdSurvey_ts_block — Survey Block",
  gdSurvey_old_ts_no: "gdSurvey_old_ts_no — Survey Old TS No",
  gdSurvey_old_ts_s_no: "gdSurvey_old_ts_s_no — Survey Old Survey No (TS type)",
  gdSurvey_ts_no: "gdSurvey_ts_no — Survey TS No",
  gdSurvey_natham_survey_no: "gdSurvey_natham_survey_no — Survey Natham No",
  gdSurvey_old_natham_survey_no: "gdSurvey_old_natham_survey_no — Survey Old Natham No",
  gdSurvey_unit: "gdSurvey_unit — Survey Unit",

  gdBoundary_north: "gdBoundary_north — Boundary North",
  gdBoundary_south: "gdBoundary_south — Boundary South",
  gdBoundary_east: "gdBoundary_east — Boundary East",
  gdBoundary_west: "gdBoundary_west — Boundary West",
  gdBoundary_land_aries: "gdBoundary_land_aries — Boundary Area",
  gdBoundary_direction: "gdBoundary_direction — Boundary Direction Of/By",
  gdBoundary_unit: "gdBoundary_unit — Boundary Unit",
  gdBoundary_measurement: "gdBoundary_measurement — Boundary Measurement",
  gdBoundary_plot_item_no: "gdBoundary_plot_item_no — Boundary Plot/Item No",
  gdBoundary_boundaryNoType: "gdBoundary_boundaryNoType — Boundary No Type",
  gdBoundary_boundary_as_per: "gdBoundary_boundary_as_per — Boundary As Per (stored value)",
  gdBoundary_asperVAO: "gdBoundary_asperVAO — As Per VAO Boundary Certificate",
  gdBoundary_asperVR: "gdBoundary_asperVR — As Per Valuation Report",
  gdBoundary_asperTS: "gdBoundary_asperTS — Triangle Shape",
  gdBoundary_asperRP: "gdBoundary_asperRP — For Road Purpose",

  gdMeasure_measurement: "gdMeasure_measurement — Measurement value",
  gdMeasure_m_unit: "gdMeasure_m_unit — Measurement unit",
  gdMeasure_l_unit: "gdMeasure_l_unit — Measurement length unit",
  gdMeasure_plot_item_no: "gdMeasure_plot_item_no — Measurement Plot/Item No",
  gdMeasure_measureNoType: "gdMeasure_measureNoType — Measurement No Type",
  gdMeasDir_label_name: "gdMeasDir_label_name — Measurement direction label",
  gdMeasDir_value: "gdMeasDir_value — Measurement direction value",
  gdMeasDir_measure_custom: "gdMeasDir_measure_custom — Measurement direction custom text",
};

/** All gd* hierarchy tags for Dynamic Field Value dropdown. */
export const SURVEY_HIERARCHY_DYNAMIC_TAGS: { id: string; name: string }[] = Object.keys(
  SURVEY_HIERARCHY_DYNAMIC_TAG_LABELS
).map((id) => ({ id, name: SURVEY_HIERARCHY_DYNAMIC_TAG_LABELS[id] }));

export const SURVEY_HIERARCHY_DYNAMIC_TAG_IDS = new Set(
  SURVEY_HIERARCHY_DYNAMIC_TAGS.map((t) => t.id)
);

/** gdSchedule_* tags writable on a schedule row via nature-scoped flat updates. */
export function isSurveyNatureScopedScheduleTag(tag: string): boolean {
  const t = strVal(tag);
  return t.startsWith("gdSchedule_") && SURVEY_HIERARCHY_TAG_TO_ROW_KEY[t] !== "";
}

export interface GeneralDetailsDocumentHierarchy {
  fd_id?: number;
  doc_no?: string;
  ds_id?: number;
  schedules?: GeneralDetailsScheduleRow[];
}

export interface GeneralDetailsScheduleRow {
  id?: number;
  nature_doc?: string;
  door_no?: string;
  flat_no?: string;
  floor?: string;
  appt_name?: string;
  buildup?: string;
  uds?: string;
  address?: string;
  remark?: string;
  schedule_id?: number;
  total_extent?: string;
  extent_unit?: string;
  applicant_name?: string;
  survey_details?: GeneralDetailsSurveyRow[];
}

/** Human-readable schedule index (API `schedule_id` is often 0-based). */
export function scheduleDisplayNumber(
  sched: GeneralDetailsScheduleRow,
  schedIdx: number
): number {
  const pos = sched.schedule_id;
  if (pos == null || pos === ("" as unknown as number)) return schedIdx + 1;
  const n = Number(pos);
  if (!Number.isFinite(n)) return schedIdx + 1;
  return n <= 0 ? schedIdx + 1 : n;
}

export interface SurveyDocumentMeta {
  fdId: string;
  docNo: string;
  dsId?: string;
}

/** Resolve scrutiny document number for a file document (`fd_id`). */
export function resolveSurveyDocumentMeta(
  scrutinyApiData: any,
  fdId: string | number | null | undefined
): SurveyDocumentMeta | null {
  const fd = strVal(fdId);
  if (!fd) return null;

  const pickDocNo = (row: Record<string, unknown> | null | undefined): string => {
    if (!row || typeof row !== "object") return "";
    return strVal(
      row.doc_no ??
        row.docNo ??
        row.deedReference ??
        row.deed_reference ??
        row.fileNo
    );
  };

  let docNo = "";
  let dsId: string | undefined;

  const gdBlock = scrutinyApiData?.generalDetailsByDocument?.[fd];
  if (gdBlock && typeof gdBlock === "object") {
    docNo = pickDocNo(gdBlock as Record<string, unknown>);
    dsId = strVal((gdBlock as Record<string, unknown>).ds_id) || undefined;
  }

  const surveyDocs = Array.isArray(scrutinyApiData?.surveyDocuments)
    ? scrutinyApiData.surveyDocuments
    : [];
  const match = surveyDocs.find((d: any) => strVal(d?.fd_id) === fd);
  if (!docNo) docNo = pickDocNo(match as Record<string, unknown>);
  if (!dsId) dsId = strVal(match?.ds_id) || undefined;

  if (!docNo) {
    const titleDeed = Array.isArray(scrutinyApiData?.surveyNoVerification?.titleDeed)
      ? scrutinyApiData.surveyNoVerification.titleDeed
      : [];
    const td = titleDeed.find((r: any) => strVal(r?.fd_id) === fd);
    docNo = pickDocNo(td);
  }

  if (!docNo && scrutinyApiData?.deed && typeof scrutinyApiData.deed === "object") {
    for (const key of Object.keys(scrutinyApiData.deed)) {
      const rows = Array.isArray(scrutinyApiData.deed[key]) ? scrutinyApiData.deed[key] : [];
      const deedRow = rows.find(
        (r: any) =>
          strVal(r?.fd_id) === fd ||
          strVal(r?.fdId) === fd ||
          strVal(r?.base_id) === fd ||
          strVal(r?.baseId) === fd
      );
      if (deedRow) {
        docNo = pickDocNo(deedRow);
        if (docNo) break;
      }
    }
  }

  if (!docNo && !match && !dsId) return null;
  return {
    fdId: fd,
    docNo,
    dsId,
  };
}

export interface GeneralDetailsSurveyRow {
  sur_id?: number;
  survey_no?: string;
  old_survey_no?: string;
  survey_type?: string;
  revenue_record?: string;
  extent?: string;
  sur_extent_unit?: string;
  plot_no?: string;
  layout_name?: string;
  ts_street_name?: string;
  ts_ward?: string;
  ts_block?: string;
  old_ts_no?: string;
  old_ts_s_no?: string;
  ts_old_survey_no?: boolean | string;
  ts_old_ts_no?: boolean | string;
  ts_no?: string;
  natham_survey_no?: string;
  boundary_details?: GeneralDetailsBoundaryRow[];
}

export interface GeneralDetailsBoundaryRow {
  measu_id?: number;
  north?: string;
  south?: string;
  east?: string;
  west?: string;
  land_aries?: string;
  direction?: string;
  unit?: string;
  measurement?: string;
  measurement_list?: GeneralDetailsMeasurementRow[];
}

export interface GeneralDetailsMeasurementRow {
  measurement_id?: number;
  measurement?: string;
  m_unit?: string;
  l_unit?: string;
  plot_item_no?: string;
  meas_dir_list?: GeneralDetailsMeasDirRow[];
}

export interface GeneralDetailsMeasDirRow {
  label?: string | number;
  label_name?: string;
  value?: string;
  measure_custom?: string;
  meas_dir_id?: number;
}

function strVal(v: unknown): string {
  if (v == null || typeof v === "object") return "";
  return String(v).trim();
}

function normalizeSurveyTagId(tag: string | number | undefined | null): string {
  let t = strVal(tag);
  if (!t) return "";
  const emDash = t.indexOf(" — ");
  if (emDash > 0) t = t.slice(0, emDash).trim();
  const hyphen = t.indexOf(" - ");
  if (hyphen > 0) t = t.slice(0, hyphen).trim();
  return t;
}

function normalizeTagRow(source: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  Object.keys(source || {}).forEach((k) => {
    out[k] = strVal(source[k]);
  });
  return out;
}

export function normalizeDirectionLabel(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Canonical master direction keys (see `measure_get.php`). */
export const STANDARD_MEASUREMENT_DIRECTION_KEYS = new Set([
  "northeastwest",
  "southeastwest",
  "eastsouthnorth",
  "westsouthnorth",
]);

export function isStandardMeasurementDirectionKey(key: string): boolean {
  return STANDARD_MEASUREMENT_DIRECTION_KEYS.has(normalizeDirectionLabel(key));
}

export type MeasurementDirectionSaveEntry = {
  labelKey: string;
  value: string;
  measDirId?: number;
  measureCustom?: string;
};

function parseMeasDirIdFromRow(row: Record<string, string>, key: string): number {
  const n = parseInt(String(row[key] ?? "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function resolveMeasDirIdForLabelKey(
  row: Record<string, string>,
  labelKey: string,
  displayLabel: string
): number {
  const wanted = new Set(
    [labelKey, displayLabel].map((v) => normalizeDirectionLabel(v)).filter(Boolean)
  );
  if (!wanted.size) return 0;

  for (const key of Object.keys(row)) {
    if (!key.startsWith(MEAS_DIR_ID_BY_LABEL_PREFIX)) continue;
    const suffix = key.slice(MEAS_DIR_ID_BY_LABEL_PREFIX.length);
    if (wanted.has(normalizeDirectionLabel(suffix))) {
      const id = parseMeasDirIdFromRow(row, key);
      if (id > 0) return id;
    }
  }

  for (const key of Object.keys(row)) {
    if (!key.startsWith(MEAS_DIR_BY_LABEL_PREFIX)) continue;
    const dirLabel = key.slice(MEAS_DIR_BY_LABEL_PREFIX.length);
    if (!wanted.has(normalizeDirectionLabel(dirLabel))) continue;
    const norm = normalizeDirectionLabel(dirLabel);
    const direct = parseMeasDirIdFromRow(row, `${MEAS_DIR_ID_BY_LABEL_PREFIX}${norm}`);
    if (direct > 0) return direct;
    for (const idKey of Object.keys(row)) {
      if (!idKey.startsWith(MEAS_DIR_ID_BY_LABEL_PREFIX)) continue;
      if (normalizeDirectionLabel(idKey.slice(MEAS_DIR_ID_BY_LABEL_PREFIX.length)) === norm) {
        const id = parseMeasDirIdFromRow(row, idKey);
        if (id > 0) return id;
      }
    }
  }
  return 0;
}

/** Build a write-back entry for one measurement direction checklist field. */
export function buildMeasurementDirectionSaveEntry(
  field: ChecklistFieldDef,
  row: Record<string, string>,
  rawValue: string
): MeasurementDirectionSaveEntry | null {
  const value = String(rawValue ?? "").trim();
  if (!value) return null;

  const displayLabel = String(
    row[`${MEAS_DIR_CUSTOM_LABEL_BY_FIELD_PREFIX}${field.id}`] ?? field.label ?? ""
  ).trim();

  let labelKey = "";
  const tag = resolveSurveyMappedFieldTag(field);
  if (tag && isMeasurementDirectionTag(tag)) {
    labelKey = tag.slice(MEASUREMENT_DIRECTION_TAG_PREFIX.length);
  } else if (displayLabel && isMeasurementDirectionLabel(displayLabel)) {
    labelKey = normalizeDirectionLabel(displayLabel);
  } else {
    return null;
  }

  let measureCustom = "";
  let measDirId = resolveMeasDirIdForLabelKey(row, labelKey, displayLabel);

  if (labelKey === "custom" || !isStandardMeasurementDirectionKey(labelKey)) {
    measureCustom = displayLabel || labelKey;
    labelKey = "custom";
    if (measDirId <= 0) {
      const { labels } = parseCustomMeasurementLists(row);
      const normCustom = normalizeDirectionLabel(measureCustom);
      const listIdx = labels.findIndex((l) => normalizeDirectionLabel(l) === normCustom);
      if (listIdx >= 0) {
        measDirId = parseMeasDirIdFromRow(
          row,
          `${MEAS_DIR_ID_BY_LABEL_PREFIX}custom_${listIdx}`
        );
      }
      if (measDirId <= 0) {
        measDirId =
          parseMeasDirIdFromRow(
            row,
            `${MEAS_DIR_ID_BY_LABEL_PREFIX}custom_${normalizeDirectionLabel(measureCustom)}`
          ) || parseMeasDirIdFromRow(row, `${MEAS_DIR_ID_BY_LABEL_PREFIX}custom`);
      }
    }
  }

  return {
    labelKey,
    value,
    ...(measureCustom ? { measureCustom } : {}),
    ...(measDirId > 0 ? { measDirId } : {}),
  };
}

export const MEASUREMENT_DIRECTION_TAG_PREFIX = "gdMeasDir_label__";

const MEAS_DIR_BY_LABEL_PREFIX = "__meas_dir_label__";
export const MEAS_DIR_CUSTOM_LABELS_KEY = "__meas_dir_custom_labels__";
export const MEAS_DIR_CUSTOM_VALUES_KEY = "__meas_dir_custom_values__";
export const MEAS_DIR_CUSTOM_LABELS_JSON_KEY = "__meas_dir_custom_labels_json__";
export const MEAS_DIR_CUSTOM_VALUES_JSON_KEY = "__meas_dir_custom_values_json__";
export const MEAS_DIR_CUSTOM_LABEL_BY_FIELD_PREFIX = "__meas_dir_custom_label_for_field__";
export const MEAS_DIR_ID_BY_LABEL_PREFIX = "__meas_dir_id__";

/** True when a label names a measurement direction pair (e.g. North - East West). */
export function isMeasurementDirectionLabel(label: string | undefined): boolean {
  const norm = String(label ?? "")
    .trim()
    .toLowerCase();
  if (!norm) return false;
  const cards = ["north", "south", "east", "west"];
  return cards.filter((c) => norm.includes(c)).length >= 2;
}

export function isMeasurementDirectionTag(tag: string): boolean {
  const t = normalizeSurveyTagId(tag);
  return t.startsWith(MEASUREMENT_DIRECTION_TAG_PREFIX);
}

/** Resolve a per-label measurement direction tag to its numeric value. */
export function resolveMeasurementDirectionTagValue(
  tag: string,
  source: Record<string, string>
): string {
  const normalizedTag = normalizeSurveyTagId(tag);
  if (!normalizedTag.startsWith(MEASUREMENT_DIRECTION_TAG_PREFIX)) return "";
  const wanted = normalizedTag.slice(MEASUREMENT_DIRECTION_TAG_PREFIX.length);
  if (!wanted) return "";

  const direct = strVal(source[`${MEAS_DIR_BY_LABEL_PREFIX}${wanted}`]);
  if (direct) return direct;

  for (const key of Object.keys(source)) {
    if (!key.startsWith(MEAS_DIR_BY_LABEL_PREFIX)) continue;
    const dirLabel = key.slice(MEAS_DIR_BY_LABEL_PREFIX.length);
    if (normalizeDirectionLabel(dirLabel) === wanted) {
      return strVal(source[key]);
    }
  }
  return "";
}

/** Build Dynamic Field Value options from measurement master labels (`measure_get.php`). */
export function buildMeasurementDirectionDynamicTags(
  labels: string[]
): { id: string; name: string }[] {
  const seen = new Set<string>();
  const out: { id: string; name: string }[] = [];
  labels.forEach((label) => {
    const trimmed = strVal(label);
    if (!trimmed) return;
    const norm = normalizeDirectionLabel(trimmed);
    if (!norm || seen.has(norm)) return;
    seen.add(norm);
    out.push({
      id: `${MEASUREMENT_DIRECTION_TAG_PREFIX}${norm}`,
      name: `${MEASUREMENT_DIRECTION_TAG_PREFIX}${norm} — ${trimmed}`,
    });
  });
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** One hierarchy row per measurement; directions are merged as label-keyed values. */
function flattenMeasurementRow(meas: GeneralDetailsMeasurementRow): Record<string, string> {
  const row = normalizeTagRow({ ...meas });
  const dirs = Array.isArray(meas.meas_dir_list) ? meas.meas_dir_list : [];
  const customLabels: string[] = [];
  const customValues: string[] = [];
  let customIndex = 0;
  dirs.forEach((dir, idx) => {
    const label = strVal(dir.label_name);
    const value = strVal(dir.value);
    const custom = strVal(dir.measure_custom);
    const normLabel = normalizeDirectionLabel(label);
    const labelId = strVal(dir.label);
    const isCustom = normLabel === "custom" || labelId === "8";
    if (label) {
      row[`${MEAS_DIR_BY_LABEL_PREFIX}${label}`] = value;
      row[`${MEAS_DIR_BY_LABEL_PREFIX}${normLabel}`] = value;
    }
    if (isCustom && custom) {
      row[`${MEAS_DIR_BY_LABEL_PREFIX}${custom}`] = value;
      row[`${MEAS_DIR_BY_LABEL_PREFIX}${normalizeDirectionLabel(custom)}`] = value;
    }
    const measDirId = strVal(dir.meas_dir_id);
    if (measDirId && normLabel) {
      row[`${MEAS_DIR_ID_BY_LABEL_PREFIX}${normLabel}`] = measDirId;
    }
    if (measDirId && isCustom) {
      row[`${MEAS_DIR_ID_BY_LABEL_PREFIX}custom_${customIndex}`] = measDirId;
      if (custom) {
        row[`${MEAS_DIR_ID_BY_LABEL_PREFIX}custom_${normalizeDirectionLabel(custom)}`] = measDirId;
      }
      customIndex += 1;
    }
    if (isCustom) {
      if (custom) customLabels.push(custom);
      if (value) customValues.push(value);
    }
    if (idx === 0) {
      row.label_name = label;
      row.value = value;
      row.measure_custom = custom;
    }
  });
  if (customLabels.length) {
    row[MEAS_DIR_CUSTOM_LABELS_KEY] = customLabels.join(", ");
    row.measure_custom = customLabels.join(", ");
    row[MEAS_DIR_CUSTOM_LABELS_JSON_KEY] = JSON.stringify(customLabels);
  }
  if (customValues.length) {
    row[MEAS_DIR_CUSTOM_VALUES_KEY] = customValues.join(", ");
    row[MEAS_DIR_CUSTOM_VALUES_JSON_KEY] = JSON.stringify(customValues);
  }
  return row;
}

export function normalizePrefillValueForField(field: ChecklistFieldDef, raw: string): string {
  const val = strVal(raw);
  if (!val) return "";
  if (field.type === "dropdown" && Array.isArray(field.options) && field.options.length) {
    return normalizeMappedDropdownValue(field, val);
  }
  if (field.type === "checkbox") {
    if (isTruthySurveyFlag(val)) return "true";
    if (String(val).trim().toLowerCase() === "false" || val === "0") return "false";
    return "";
  }
  if (field.type === "yes_no") {
    const lower = val.toLowerCase();
    if (lower === "yes" || lower === "true" || lower === "1") return "Yes";
    if (lower === "no" || lower === "false" || lower === "0") return "No";
  }
  return val;
}

/** General Details boundary `direction` column uses `By` / `Of` (see MeasurementItem.tsx). */
export function normalizeBoundaryDirectionForGdStorage(value: string | undefined | null): string {
  const v = strVal(value);
  if (!v) return "";
  const lower = v.toLowerCase();
  if (lower === "by") return "By";
  if (lower === "of") return "Of";
  return v;
}

/** Stored in `tbl_general_details.schedule_as_per` (General Details schedule checkbox). */
export const SCHEDULE_AS_PER_PATTA_VALUE = "As Per Patta";
export const SCHEDULE_AS_PER_DOC_NO_VALUE = "As Per Doc No";

export const SCHEDULE_AS_PER_BY_TAG: Record<string, string> = {
  gdSchedule_asPerPatta: SCHEDULE_AS_PER_PATTA_VALUE,
};

export function isScheduleAsPerTag(tag: string): boolean {
  return Object.prototype.hasOwnProperty.call(SCHEDULE_AS_PER_BY_TAG, normalizeSurveyTagId(tag));
}

function scheduleAsPerMatchesStored(stored: string, expected: string): boolean {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  const a = norm(stored);
  const b = norm(expected);
  return a === b || a.includes(b) || b.includes(a);
}

function scheduleAsPerTagValue(tag: string, source: Record<string, string>): string {
  const normalizedTag = normalizeSurveyTagId(tag);
  const expected = SCHEDULE_AS_PER_BY_TAG[normalizedTag];
  if (expected) {
    const stored = strVal(source.scheduleasper);
    if (stored) return scheduleAsPerMatchesStored(stored, expected) ? "true" : "false";
    if (isTruthySurveyFlag(source.asPerPatta)) return "true";
    if (strVal(source.asPerPatta).toLowerCase() === "false") return "false";
    return "";
  }
  if (normalizedTag === "gdSchedule_scheduleasper") {
    const stored = strVal(source.scheduleasper);
    if (!stored) return "";
    if (scheduleAsPerMatchesStored(stored, SCHEDULE_AS_PER_PATTA_VALUE)) return "true";
    if (scheduleAsPerMatchesStored(stored, SCHEDULE_AS_PER_DOC_NO_VALUE)) return "false";
    return stored;
  }
  return "";
}

export function resolveScheduleAsPerFromTaggedFields(
  fields: Record<string, string>
): string {
  const patta = strVal(fields.gdSchedule_asPerPatta);
  if (isTruthySurveyFlag(patta)) return SCHEDULE_AS_PER_PATTA_VALUE;
  if (patta.toLowerCase() === "false") return SCHEDULE_AS_PER_DOC_NO_VALUE;
  return "";
}

/** Stored in `tbl_measure_details.boundary_as_per` (mutually exclusive checkboxes). */
export const BOUNDARY_AS_PER_BY_TAG: Record<string, string> = {
  gdBoundary_asperVAO: "[As Per VAO Boundary Certificate]",
  gdBoundary_asperVR: "[As Per Valuation Report]",
  gdBoundary_asperTS: "[Triangle Shape]",
  gdBoundary_asperRP: "[For Road Purpose]",
};

export function isBoundaryAsPerTag(tag: string): boolean {
  return Object.prototype.hasOwnProperty.call(BOUNDARY_AS_PER_BY_TAG, normalizeSurveyTagId(tag));
}

function boundaryAsPerMatchesStored(stored: string, expected: string): boolean {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[\[\]]/g, "")
      .replace(/[^a-z0-9]/g, "");
  const a = norm(stored);
  const b = norm(expected);
  return a === b || a.includes(b) || b.includes(a);
}

function boundaryAsPerTagValue(tag: string, source: Record<string, string>): string {
  const normalizedTag = normalizeSurveyTagId(tag);
  const expected = BOUNDARY_AS_PER_BY_TAG[normalizedTag];
  if (!expected) return "";
  const stored = strVal(source.boundary_as_per);
  if (stored) return boundaryAsPerMatchesStored(stored, expected) ? "true" : "false";
  const rowKey = resolveSurveyTagRowKey(normalizedTag);
  if (rowKey && isTruthySurveyFlag(source[rowKey])) return "true";
  if (rowKey && strVal(source[rowKey]).toLowerCase() === "false") return "false";
  return "";
}

export function resolveBoundaryAsPerFromTaggedFields(
  fields: Record<string, string>
): string {
  const order = [
    "gdBoundary_asperVAO",
    "gdBoundary_asperVR",
    "gdBoundary_asperTS",
    "gdBoundary_asperRP",
  ];
  for (const tag of order) {
    const val = strVal(fields[tag]);
    if (isTruthySurveyFlag(val)) return BOUNDARY_AS_PER_BY_TAG[tag];
  }
  return "";
}

function hierarchyPrefillFieldUnset(
  field: ChecklistFieldDef,
  value: string | undefined | null
): boolean {
  const text = value != null ? String(value).trim() : "";
  if (!text) return true;
  if (field.type === "checkbox" && text === "false") return true;
  return false;
}

function valueForMeasurementDirectionField(
  field: ChecklistFieldDef,
  source: Record<string, string>
): string {
  const fieldLabel = strVal(field.label);
  if (!fieldLabel) return "";
  const normalizedField = normalizeDirectionLabel(fieldLabel);
  if (normalizedField === "custom") {
    const jsonValues = strVal(source[MEAS_DIR_CUSTOM_VALUES_JSON_KEY]);
    if (jsonValues.startsWith("[")) {
      try {
        const parsed = JSON.parse(jsonValues);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const first = strVal(parsed[0]);
          if (first) return first;
        }
      } catch {
        // fall through to flat keys
      }
    }
    const customValues = strVal(source[MEAS_DIR_CUSTOM_VALUES_KEY]);
    if (customValues) return customValues;
    const directCustomValue = strVal(source[`${MEAS_DIR_BY_LABEL_PREFIX}custom`]);
    if (directCustomValue) return directCustomValue;
  }

  const exact = source[`${MEAS_DIR_BY_LABEL_PREFIX}${fieldLabel}`];
  if (exact) return exact;

  const normalized = source[`${MEAS_DIR_BY_LABEL_PREFIX}${normalizedField}`];
  if (normalized) return normalized;

  for (const key of Object.keys(source)) {
    if (!key.startsWith(MEAS_DIR_BY_LABEL_PREFIX)) continue;
    const dirLabel = key.slice(MEAS_DIR_BY_LABEL_PREFIX.length);
    if (normalizeDirectionLabel(dirLabel) === normalizedField) {
      return strVal(source[key]);
    }
  }

  const { labels, values } = parseCustomMeasurementLists(source);
  const customIdx = labels.findIndex(
    (label) => normalizeDirectionLabel(label) === normalizedField
  );
  if (customIdx >= 0 && values[customIdx]) {
    return strVal(values[customIdx]);
  }
  return "";
}

/** Flat rows for each hierarchy level (includes parent meta keys). */
export function flattenSurveyHierarchyRows(
  docHierarchy: GeneralDetailsDocumentHierarchy | null | undefined,
  fdId: string | number,
  docNo?: string
): {
  schedule: Record<string, string>[];
  survey: Record<string, string>[];
  boundary: Record<string, string>[];
  measurement: Record<string, string>[];
} {
  const fd = strVal(fdId);
  const schedules = Array.isArray(docHierarchy?.schedules) ? docHierarchy!.schedules! : [];
  const scheduleRows: Record<string, string>[] = [];
  const surveyRows: Record<string, string>[] = [];
  const boundaryRows: Record<string, string>[] = [];
  const measurementRows: Record<string, string>[] = [];

  schedules.forEach((sched) => {
    const scheduleId = strVal(sched.id);
    const natureKey = matchNaturePropertyKey(sched.nature_doc ?? "") ?? "";
    const schedRow = normalizeTagRow(sched as Record<string, unknown>);
    schedRow[SURVEY_HIERARCHY_META.FD_ID] = fd;
    schedRow[SURVEY_HIERARCHY_META.SCHEDULE_ID] = scheduleId;
    schedRow[SURVEY_HIERARCHY_META.NATURE_KEY] = natureKey;
    schedRow[SURVEY_HIERARCHY_META.SCHEDULE_POS] = strVal(sched.schedule_id);
    if (docNo) schedRow[SURVEY_HIERARCHY_META.DOC_NO] = docNo;
    scheduleRows.push(schedRow);

    const surveys = Array.isArray(sched.survey_details) ? sched.survey_details : [];
    surveys.forEach((sur) => {
      const surId = strVal(sur.sur_id);
      const surRow = normalizeTagRow(sur as Record<string, unknown>);
      surRow[SURVEY_HIERARCHY_META.FD_ID] = fd;
      surRow[SURVEY_HIERARCHY_META.SCHEDULE_ID] = scheduleId;
      surRow[SURVEY_HIERARCHY_META.SUR_ID] = surId;
      surRow[SURVEY_HIERARCHY_META.NATURE_KEY] = natureKey;
      surveyRows.push(surRow);

      const boundaries = Array.isArray(sur.boundary_details) ? sur.boundary_details : [];
      boundaries.forEach((bnd) => {
        const bndId = strVal(bnd.measu_id);
        const bndRow = normalizeTagRow(bnd as Record<string, unknown>);
        bndRow[SURVEY_HIERARCHY_META.FD_ID] = fd;
        bndRow[SURVEY_HIERARCHY_META.SCHEDULE_ID] = scheduleId;
        bndRow[SURVEY_HIERARCHY_META.SUR_ID] = surId;
        bndRow[SURVEY_HIERARCHY_META.BOUNDARY_ID] = bndId;
        bndRow[SURVEY_HIERARCHY_META.NATURE_KEY] = natureKey;
        boundaryRows.push(bndRow);

        const measurements = Array.isArray(bnd.measurement_list) ? bnd.measurement_list : [];
        measurements.forEach((meas) => {
          const measRow = flattenMeasurementRow(meas);
          measRow[SURVEY_HIERARCHY_META.FD_ID] = fd;
          measRow[SURVEY_HIERARCHY_META.SCHEDULE_ID] = scheduleId;
          measRow[SURVEY_HIERARCHY_META.SUR_ID] = surId;
          measRow[SURVEY_HIERARCHY_META.BOUNDARY_ID] = bndId;
          measRow[SURVEY_HIERARCHY_META.MEASUREMENT_ID] = strVal(meas.measurement_id);
          measRow[SURVEY_HIERARCHY_META.NATURE_KEY] = natureKey;
          measurementRows.push(measRow);
        });
      });
    });
  });

  return { schedule: scheduleRows, survey: surveyRows, boundary: boundaryRows, measurement: measurementRows };
}

export function hierarchyRowIdentity(level: SurveyHierarchyLevel, row: Record<string, string>): string {
  const parts = [row[SURVEY_HIERARCHY_META.FD_ID] ?? ""];
  if (level === "schedule") {
    parts.push(row[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "");
  } else if (level === "survey") {
    parts.push(row[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "", row[SURVEY_HIERARCHY_META.SUR_ID] ?? "");
  } else if (level === "boundary") {
    parts.push(
      row[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "",
      row[SURVEY_HIERARCHY_META.SUR_ID] ?? "",
      row[SURVEY_HIERARCHY_META.BOUNDARY_ID] ?? ""
    );
  } else {
    parts.push(
      row[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "",
      row[SURVEY_HIERARCHY_META.SUR_ID] ?? "",
      row[SURVEY_HIERARCHY_META.BOUNDARY_ID] ?? "",
      row[SURVEY_HIERARCHY_META.MEASUREMENT_ID] ?? ""
    );
  }
  return parts.join("|");
}

/** Fill missing `__sv_*__` meta on a hierarchy row from General Detail flat rows. */
export function patchHierarchyRowMeta(
  level: SurveyHierarchyLevel,
  row: Record<string, string>,
  flatRows: ReturnType<typeof flattenSurveyHierarchyRows>
): Record<string, string> {
  const patched = { ...row };
  const sources = flatRows[level] || [];
  if (!sources.length) return patched;

  const fullId = hierarchyRowIdentity(level, patched);
  let match = fullId.replace(/\|/g, "").length
    ? sources.find((s) => hierarchyRowIdentity(level, s) === fullId)
    : undefined;

  if (!match) {
    match = sources.find((source) =>
      Object.values(SURVEY_HIERARCHY_META).every((metaKey) => {
        const expected = strVal(patched[metaKey]);
        if (!expected) return true;
        return strVal(source[metaKey]) === expected;
      })
    );
  }

  if (!match && sources.length === 1) {
    match = sources[0];
  }

  if (match) {
    Object.values(SURVEY_HIERARCHY_META).forEach((metaKey) => {
      const val = strVal(match![metaKey]);
      if (val) patched[metaKey] = val;
    });
    // Only patch measurement direction row ids from GD — never overwrite edited values/labels.
    Object.keys(match).forEach((key) => {
      if (key.startsWith("__meas_dir_id__") || key.startsWith("__sv_")) {
        const val = strVal(match![key]);
        if (val) patched[key] = val;
      }
    });
  }
  return patched;
}

/** Fill missing child hierarchy ids (survey/boundary/measurement) from GD flat rows. */
export function enrichHierarchyRowMeta(
  level: SurveyHierarchyLevel,
  row: Record<string, string>,
  flatRows: ReturnType<typeof flattenSurveyHierarchyRows>
): Record<string, string> {
  const enriched = { ...row };
  const fdId = strVal(enriched[SURVEY_HIERARCHY_META.FD_ID]);
  const scheduleId = strVal(enriched[SURVEY_HIERARCHY_META.SCHEDULE_ID]);
  if (!fdId || !scheduleId) return enriched;

  const sameSchedule = (r: Record<string, string>) =>
    strVal(r[SURVEY_HIERARCHY_META.FD_ID]) === fdId &&
    strVal(r[SURVEY_HIERARCHY_META.SCHEDULE_ID]) === scheduleId;

  if (!strVal(enriched[SURVEY_HIERARCHY_META.SUR_ID])) {
    const survey = flatRows.survey.find(sameSchedule);
    if (survey) {
      enriched[SURVEY_HIERARCHY_META.SUR_ID] = strVal(survey[SURVEY_HIERARCHY_META.SUR_ID]);
    }
  }

  const surId = strVal(enriched[SURVEY_HIERARCHY_META.SUR_ID]);
  if (!strVal(enriched[SURVEY_HIERARCHY_META.BOUNDARY_ID]) && surId) {
    const boundary = flatRows.boundary.find(
      (b) => sameSchedule(b) && strVal(b[SURVEY_HIERARCHY_META.SUR_ID]) === surId
    );
    if (boundary) {
      enriched[SURVEY_HIERARCHY_META.BOUNDARY_ID] = strVal(
        boundary[SURVEY_HIERARCHY_META.BOUNDARY_ID]
      );
    }
  }

  const boundaryId = strVal(enriched[SURVEY_HIERARCHY_META.BOUNDARY_ID]);
  if (!strVal(enriched[SURVEY_HIERARCHY_META.MEASUREMENT_ID]) && boundaryId) {
    const measurement = flatRows.measurement.find(
      (m) =>
        sameSchedule(m) &&
        strVal(m[SURVEY_HIERARCHY_META.SUR_ID]) === surId &&
        strVal(m[SURVEY_HIERARCHY_META.BOUNDARY_ID]) === boundaryId
    );
    if (measurement) {
      enriched[SURVEY_HIERARCHY_META.MEASUREMENT_ID] = strVal(
        measurement[SURVEY_HIERARCHY_META.MEASUREMENT_ID]
      );
    }
  }

  return enriched;
}

export type SurveyHierarchyRowMetaIds = {
  scheduleId?: number;
  surId?: number;
  boundaryId?: number;
  measurementId?: number;
};

export function buildHierarchyRowMetaIds(row: Record<string, string>): SurveyHierarchyRowMetaIds {
  const parseMetaId = (key: string): number | undefined => {
    const n = parseInt(strVal(row[key]), 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };
  return {
    scheduleId: parseMetaId(SURVEY_HIERARCHY_META.SCHEDULE_ID),
    surId: parseMetaId(SURVEY_HIERARCHY_META.SUR_ID),
    boundaryId: parseMetaId(SURVEY_HIERARCHY_META.BOUNDARY_ID),
    measurementId: parseMetaId(SURVEY_HIERARCHY_META.MEASUREMENT_ID),
  };
}

/** Resolve DB row id for a gd* hierarchy tag using row meta (not UI group level). */
export function resolveHierarchyTargetRowId(
  tag: string,
  level: SurveyHierarchyLevel,
  declaredRowId: number,
  meta: SurveyHierarchyRowMetaIds
): number {
  const t = tag.trim();
  if (!t) return 0;
  if (t.startsWith("gdSchedule_")) {
    return meta.scheduleId ?? (level === "schedule" ? declaredRowId : 0);
  }
  if (t.startsWith("gdSurvey_")) {
    return meta.surId ?? (level === "survey" ? declaredRowId : 0);
  }
  if (t.startsWith("gdBoundary_")) {
    return meta.boundaryId ?? (level === "boundary" ? declaredRowId : 0);
  }
  if (t.startsWith("gdMeasure_") || t.startsWith("gdMeasDir_")) {
    return meta.measurementId ?? (level === "measurement" ? declaredRowId : 0);
  }
  return declaredRowId;
}

/** Safe fallback aliases (format/key variants only, no cross-type field mirroring). */
const SURVEY_ROW_KEY_ALIASES: Record<string, string[]> = {
  total_extent: ["extent"],
  // Boundary "Total Extent / Boundaries" is stored as `land_aries` in many files,
  // while some dynamic mappings use `gdBoundary_measurement`.
  land_aries: ["measurement"],
  measurement: ["land_aries"],
};

/** Legacy title-deed / scrutiny keys that mirror hierarchy row keys. */
const SURVEY_ROW_KEY_LEGACY_ALIASES: Record<string, string[]> = {
  old_survey_no: ["oldSurveyNo", "old_survey"],
  old_ts_s_no: ["old_ts_survey_no"],
  survey_no: ["surveyNo", "pattaExtractSno", "new_survey_no", "current_survey"],
  layout_name: ["deedlayoutnameverfy"],
  plot_no: ["deedplotNoverfy"],
  ts_street_name: ["deedstreetnameverfy"],
  extent: ["deedExtentverfy", "measurementextentmatch"],
  door_no: ["deedDoorNoverfy"],
  flat_no: ["deedflatnoverfy"],
  appt_name: ["deedapartnameverfy"],
  north: ["boundverfyNorth"],
  south: ["boundverfySouth"],
  east: ["boundverfyEast"],
  west: ["boundverfyWest"],
};

function pickFirstNonEmpty(source: Record<string, string>, keys: string[]): string {
  const seen = new Set<string>();
  for (const key of keys) {
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const val = strVal(source[key]);
    if (val) return val;
  }
  return "";
}

function mapTagToSourceRow(tag: string, source: Record<string, string>): string {
  const normalizedTag = normalizeSurveyTagId(tag);
  if (normalizedTag === "gdMeasDir_measure_custom") {
    return strVal(source[MEAS_DIR_CUSTOM_LABELS_KEY] ?? source.measure_custom);
  }
  if (normalizedTag === "gdMeasDir_value") {
    return strVal(source[MEAS_DIR_CUSTOM_VALUES_KEY] ?? source.value);
  }
  if (isMeasurementDirectionTag(normalizedTag)) {
    return resolveMeasurementDirectionTagValue(normalizedTag, source);
  }
  if (normalizedTag === "gdSurvey_old_survey_no") {
    const surveyType = normalizeSurveyTypeValue(source.survey_type);
    if (surveyType === "ts_no") {
      const fromTs = pickFirstNonEmpty(source, ["old_ts_s_no", "old_ts_survey_no"]);
      if (fromTs) return fromTs;
    }
  }
  if (normalizedTag === "gdSurvey_old_ts_s_no") {
    return pickFirstNonEmpty(source, ["old_ts_s_no", "old_ts_survey_no"]);
  }
  if (isBoundaryAsPerTag(normalizedTag)) {
    return boundaryAsPerTagValue(normalizedTag, source);
  }
  if (isScheduleAsPerTag(normalizedTag) || normalizedTag === "gdSchedule_scheduleasper") {
    return scheduleAsPerTagValue(normalizedTag, source);
  }
  const rowKey = resolveSurveyTagRowKey(normalizedTag);
  if (rowKey) {
    const keysToTry = [
      rowKey,
      ...(SURVEY_ROW_KEY_ALIASES[rowKey] ?? []),
      ...(SURVEY_ROW_KEY_LEGACY_ALIASES[rowKey] ?? []),
    ];
    const fromRow = pickFirstNonEmpty(source, keysToTry);
    if (fromRow) return fromRow;
  }
  return strVal(source[normalizedTag] ?? source[tag]);
}

function rowsForScheduleLevel(
  rows: Record<string, string>[],
  fdId: string,
  scheduleId: string
): Record<string, string>[] {
  return rows.filter(
    (r) =>
      strVal(r[SURVEY_HIERARCHY_META.FD_ID]) === fdId &&
      strVal(r[SURVEY_HIERARCHY_META.SCHEDULE_ID]) === scheduleId
  );
}

function mergeHierarchyRowsForPrefill(rows: Record<string, string>[]): Record<string, string> {
  const merged: Record<string, string> = {};
  rows.forEach((row) => {
    Object.keys(row).forEach((k) => {
      const v = strVal(row[k]);
      if (!v) return;
      if (k.startsWith("__sv_")) {
        merged[k] = v;
        return;
      }
      if (!merged[k]) merged[k] = v;
    });
  });
  return merged;
}

function pickPrimarySurveyRows(
  rows: Record<string, string>[],
  surId?: string
): Record<string, string>[] {
  if (!rows.length) return rows;
  if (surId) {
    const matched = rows.filter(
      (r) => strVal(r[SURVEY_HIERARCHY_META.SUR_ID]) === surId
    );
    if (matched.length) return matched;
  }
  if (rows.length === 1) return rows;
  const sorted = [...rows].sort((a, b) => {
    const aId = parseInt(strVal(a[SURVEY_HIERARCHY_META.SUR_ID]), 10) || 0;
    const bId = parseInt(strVal(b[SURVEY_HIERARCHY_META.SUR_ID]), 10) || 0;
    return bId - aId;
  });
  return [sorted[0]];
}

function childRowsForScheduleLevel(
  rows: Record<string, string>[],
  fdId: string,
  scheduleId: string,
  surId?: string
): Record<string, string>[] {
  const scoped = rowsForScheduleLevel(rows, fdId, scheduleId);
  if (!surId) return scoped;
  const matched = scoped.filter((r) => strVal(r[SURVEY_HIERARCHY_META.SUR_ID]) === surId);
  return matched.length ? matched : scoped;
}

/**
 * Merge schedule + child survey/boundary/measurement rows for nature-card prefill.
 * gdSurvey_* values live on survey rows, not the schedule row alone.
 */
export function buildScheduleNaturePrefillSource(
  schedRow: Record<string, string>,
  flatRows: {
    survey: Record<string, string>[];
    boundary: Record<string, string>[];
    measurement: Record<string, string>[];
  },
  options?: { surId?: string }
): Record<string, string> {
  const fdId = strVal(schedRow[SURVEY_HIERARCHY_META.FD_ID]);
  const scheduleId = strVal(schedRow[SURVEY_HIERARCHY_META.SCHEDULE_ID]);
  if (!fdId || !scheduleId) return { ...schedRow };

  const surId =
    strVal(options?.surId) ||
    strVal(schedRow[SURVEY_HIERARCHY_META.SUR_ID]);
  const surveyRows = pickPrimarySurveyRows(
    rowsForScheduleLevel(flatRows.survey, fdId, scheduleId),
    surId || undefined
  );
  const primarySurId = strVal(surveyRows[0]?.[SURVEY_HIERARCHY_META.SUR_ID]);
  const surveyMerged = mergeHierarchyRowsForPrefill(surveyRows);
  const boundaryMerged = mergeHierarchyRowsForPrefill(
    childRowsForScheduleLevel(flatRows.boundary, fdId, scheduleId, primarySurId)
  );
  const measurementMerged = mergeHierarchyRowsForPrefill(
    childRowsForScheduleLevel(flatRows.measurement, fdId, scheduleId, primarySurId)
  );

  return {
    ...schedRow,
    ...surveyMerged,
    ...boundaryMerged,
    ...measurementMerged,
    [SURVEY_HIERARCHY_META.FD_ID]: fdId,
    [SURVEY_HIERARCHY_META.SCHEDULE_ID]: scheduleId,
    [SURVEY_HIERARCHY_META.NATURE_KEY]: strVal(schedRow[SURVEY_HIERARCHY_META.NATURE_KEY]),
    ...(primarySurId ? { [SURVEY_HIERARCHY_META.SUR_ID]: primarySurId } : {}),
  };
}

function findScheduleRowForHierarchySource(
  flatRows: ReturnType<typeof flattenSurveyHierarchyRows>,
  source: Record<string, string>
): Record<string, string> | undefined {
  const fdId = strVal(source[SURVEY_HIERARCHY_META.FD_ID]);
  const scheduleId = strVal(source[SURVEY_HIERARCHY_META.SCHEDULE_ID]);
  if (!fdId || !scheduleId) return undefined;
  return flatRows.schedule.find(
    (r) =>
      strVal(r[SURVEY_HIERARCHY_META.FD_ID]) === fdId &&
      strVal(r[SURVEY_HIERARCHY_META.SCHEDULE_ID]) === scheduleId
  );
}

/**
 * Build prefill source for any hierarchy level.
 * Survey/Boundary/Measurement groups often contain gdSchedule_* fields
 * (buildup, uds, flat_no, etc.) that live on the parent schedule row.
 */
export function buildHierarchyPrefillSource(
  level: SurveyHierarchyLevel,
  source: Record<string, string>,
  flatRows: ReturnType<typeof flattenSurveyHierarchyRows>,
  options?: { surId?: string }
): Record<string, string> {
  if (level === "schedule") {
    return buildScheduleNaturePrefillSource(source, flatRows, options);
  }
  const schedRow = findScheduleRowForHierarchySource(flatRows, source);
  if (!schedRow) return { ...source };
  const surId = strVal(options?.surId) || strVal(source[SURVEY_HIERARCHY_META.SUR_ID]);
  const scheduleMerged = buildScheduleNaturePrefillSource(
    schedRow,
    flatRows,
    surId ? { surId } : undefined
  );
  return { ...scheduleMerged, ...source };
}

export function mapSurveyTagToSourceRow(
  tag: string,
  source: Record<string, string>
): string {
  return mapTagToSourceRow(tag, source);
}

function isGenericSurveyVerificationLabel(label: string | undefined): boolean {
  const norm = String(label ?? "")
    .trim()
    .toLowerCase();
  if (!norm) return false;
  if (norm.includes("subdivision")) return true;
  if (norm.includes("currently") && norm.includes("survey")) return true;
  if (norm.includes("changed") && norm.includes("survey")) return true;
  if (norm.includes("verified")) return true;
  if (norm.includes("certificate") && norm.includes("obtained")) return true;
  if (norm.includes("correlation") || norm.includes("corelation")) return true;
  if (norm.includes("proceeding") && norm.includes("patta")) return true;
  return false;
}

function shouldSkipSurveyTypeConditionalInference(field: ChecklistFieldDef): boolean {
  if (isGenericSurveyVerificationLabel(field.label)) return true;
  const mapped = normalizeSurveyTagId(normalizeDeedIdForSelect(field.scrutinyLinkedDeedId));
  if (!mapped && fieldHasConfiguredFollowUps(field)) return true;
  return false;
}

function inferSurveyHierarchyTagFromLabel(label: string | undefined): string | null {
  const raw = String(label ?? "").trim();
  const norm = raw.toLowerCase();
  if (!norm) return null;
  if (isGenericSurveyVerificationLabel(raw)) return null;
  if (isMeasurementDirectionLabel(raw)) {
    return `${MEASUREMENT_DIRECTION_TAG_PREFIX}${normalizeDirectionLabel(raw)}`;
  }
  if (norm.includes("door")) return "gdSchedule_door_no";
  if (norm.includes("flat")) return "gdSchedule_flat_no";
  if (norm.includes("floor")) return "gdSchedule_floor";
  if (norm.includes("apartment") || norm.includes("appt")) return "gdSchedule_appt_name";
  if (norm.includes("built") || norm.includes("buildup")) return "gdSchedule_buildup";
  if (norm.includes("uds")) return "gdSchedule_uds";
  if (norm.includes("schedule") && norm.includes("no")) return "gdSchedule_schedule_id";
  if (norm.includes("nature")) return "gdSchedule_nature_doc";
  if (norm.includes("natham") && norm.includes("survey")) {
    if (norm.includes("old")) return "gdSurvey_old_natham_survey_no";
    return "gdSurvey_natham_survey_no";
  }
  if ((norm.includes("t.s") || norm.includes("ts")) && norm.includes("no")) {
    if (norm.includes("old")) {
      if (
        /\bts\s+s\.?\s*no\b/i.test(raw) ||
        /\bold\s+survey\b/i.test(raw)
      ) {
        return "gdSurvey_old_ts_s_no";
      }
      return "gdSurvey_old_ts_no";
    }
    return "gdSurvey_ts_no";
  }
  if (norm.includes("survey") && norm.includes("type")) return "gdSurvey_survey_type";
  if (norm.includes("old") && norm.includes("survey")) return "gdSurvey_old_survey_no";
  if (norm.includes("survey") && norm.includes("no")) return "gdSurvey_survey_no";
  if (norm.includes("plot")) return "gdSurvey_plot_no";
  if (norm.includes("layout")) return "gdSurvey_layout_name";
  if (norm.includes("street")) return "gdSurvey_ts_street_name";
  if (norm.includes("north")) return "gdBoundary_north";
  if (norm.includes("south")) return "gdBoundary_south";
  if (norm.includes("east")) return "gdBoundary_east";
  if (norm.includes("west")) return "gdBoundary_west";
  if (norm.includes("as per") && norm.includes("patta")) return "gdSchedule_asPerPatta";
  if (norm.includes("patta") && norm.includes("extract")) return "gdSchedule_asPerPatta";
  if (norm.includes("vao") && norm.includes("boundary")) return "gdBoundary_asperVAO";
  if (norm.includes("valuation") && norm.includes("report")) return "gdBoundary_asperVR";
  if (norm.includes("triangle") && norm.includes("shape")) return "gdBoundary_asperTS";
  if (norm.includes("road") && norm.includes("purpose")) return "gdBoundary_asperRP";
  if (norm.includes("direction")) return "gdBoundary_direction";
  if (norm.includes("extent")) return "gdSurvey_extent";
  if (norm.includes("measurement")) return "gdMeasure_measurement";
  return null;
}

function resolveSurveyHierarchyFieldTag(field: ChecklistFieldDef): string {
  const mapped = normalizeSurveyTagId(normalizeDeedIdForSelect(field.scrutinyLinkedDeedId));
  if (mapped) return mapped;
  return inferSurveyHierarchyTagFromLabel(field.label) ?? "";
}

export function resolveSurveyMappedFieldTag(field: ChecklistFieldDef): string {
  return resolveSurveyHierarchyFieldTag(field);
}

/** Survey-type buckets — matches General Detail `SurveyItem.tsx` visibility. */
export const SURVEY_TYPE_CONDITIONAL_TAGS: Record<string, string[]> = {
  survey_no: [
    "gdSurvey_old_survey_no",
    "gdSurvey_survey_no",
    "oldSurveyNo",
    "surveyNo",
    "pattaExtractSno",
  ],
  natham_survey_no: ["gdSurvey_old_natham_survey_no", "gdSurvey_natham_survey_no"],
  ts_no: ["gdSurvey_old_ts_no", "gdSurvey_ts_no", "gdSurvey_old_ts_s_no"],
};

/** Tags for "Old Survey No." when Survey Type is TS No (not plain Survey No). */
const TS_OLD_SURVEY_NUMBER_TAGS = new Set([
  "gdSurvey_old_survey_no",
  "gdSurvey_old_ts_s_no",
]);

/** Tags for "Old TS No." when Survey Type is TS No. */
const TS_OLD_TS_NUMBER_TAGS = new Set(["gdSurvey_old_ts_no"]);

function isTruthySurveyFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  const text = String(value ?? "").trim().toLowerCase();
  return text === "true" || text === "1" || text === "yes";
}

/**
 * When Survey Type is TS No, General Details uses either Old Survey No (`old_ts_s_no`)
 * or Old TS No (`old_ts_no`) — matches `SurveyItem.tsx` ts_survey_type dropdown.
 */
export function resolveTsOldSurveyNumberVariant(
  row: Record<string, string>
): "ts_old_survey_no" | "ts_old_ts_no" | null {
  if (isTruthySurveyFlag(row.ts_old_survey_no) || isTruthySurveyFlag(row[SURVEY_HIERARCHY_META.TS_OLD_SURVEY_NO])) {
    return "ts_old_survey_no";
  }
  if (isTruthySurveyFlag(row.ts_old_ts_no) || isTruthySurveyFlag(row[SURVEY_HIERARCHY_META.TS_OLD_TS_NO])) {
    return "ts_old_ts_no";
  }
  const oldSurveyViaTs =
    strVal(row.old_ts_s_no) !== "" || strVal(row.old_ts_survey_no) !== "";
  const oldTs = strVal(row.old_ts_no) !== "";
  if (oldSurveyViaTs && !oldTs) return "ts_old_survey_no";
  if (oldTs && !oldSurveyViaTs) return "ts_old_ts_no";
  if (oldSurveyViaTs) return "ts_old_survey_no";
  if (oldTs) return "ts_old_ts_no";
  return null;
}

function hasTsOldSurveyNumberValue(row: Record<string, string>): boolean {
  return (
    isTruthySurveyFlag(row.ts_old_survey_no) ||
    isTruthySurveyFlag(row[SURVEY_HIERARCHY_META.TS_OLD_SURVEY_NO]) ||
    strVal(row.old_ts_s_no) !== "" ||
    strVal(row.old_ts_survey_no) !== ""
  );
}

function hasTsOldTsNumberValue(row: Record<string, string>): boolean {
  return (
    isTruthySurveyFlag(row.ts_old_ts_no) ||
    isTruthySurveyFlag(row[SURVEY_HIERARCHY_META.TS_OLD_TS_NO]) ||
    strVal(row.old_ts_no) !== ""
  );
}

export function surveyTypeVisibilityRowFromScheduleSurvey(
  sd:
    | {
        survey_type?: string;
        ts_old_survey_no?: unknown;
        ts_old_ts_no?: unknown;
        old_ts_s_no?: string;
        old_ts_survey_no?: string;
        old_ts_no?: string;
      }
    | undefined
    | null
): Record<string, string> {
  if (!sd) return {};
  return {
    survey_type: String(sd.survey_type ?? "").trim(),
    ts_old_survey_no: String(sd.ts_old_survey_no ?? "").trim(),
    ts_old_ts_no: String(sd.ts_old_ts_no ?? "").trim(),
    old_ts_s_no: String(sd.old_ts_s_no ?? sd.old_ts_survey_no ?? "").trim(),
    old_ts_survey_no: String(sd.old_ts_survey_no ?? sd.old_ts_s_no ?? "").trim(),
    old_ts_no: String(sd.old_ts_no ?? "").trim(),
  };
}

/** GD survey row for TS No sub-variant visibility (schedule + sur_id lookup). */
export function resolveGdSurveyVisibilityRow(
  gdByDoc: Record<string, GeneralDetailsDocumentHierarchy>,
  fdId: string | undefined,
  scheduleId: string | undefined,
  sur?: { sur_id?: string | number } | null
): Record<string, string> {
  const fromSur = surveyTypeVisibilityRowFromScheduleSurvey(
    sur as Parameters<typeof surveyTypeVisibilityRowFromScheduleSurvey>[0]
  );
  const fd = String(fdId ?? "").trim();
  const schedKey = String(scheduleId ?? "").trim();
  if (!fd || !schedKey) return fromSur;
  const sched = (gdByDoc[fd]?.schedules ?? []).find(
    (s) => String(s.id ?? "").trim() === schedKey
  );
  const surId = String(sur?.sur_id ?? "").trim();
  const match = surId
    ? (sched?.survey_details ?? []).find((sd) => String(sd.sur_id ?? "").trim() === surId)
    : sched?.survey_details?.[0];
  const fromDoc = surveyTypeVisibilityRowFromScheduleSurvey(match);
  return { ...fromSur, ...fromDoc };
}

export function mergeSurveyTypeVisibilityRow(
  row: Record<string, string>,
  gdSurveySource?: Record<string, string> | null
): Record<string, string> {
  const fromGd = gdSurveySource ? { ...gdSurveySource } : {};
  const forVariant = { ...row, ...fromGd };
  const merged = { ...row, ...fromGd };
  copyTsOldSurveyNumberMeta(merged, forVariant);
  return merged;
}

function copyTsOldSurveyNumberMeta(
  target: Record<string, string>,
  source: Record<string, string>
): void {
  const variant = resolveTsOldSurveyNumberVariant(source);
  if (variant === "ts_old_survey_no") {
    target[SURVEY_HIERARCHY_META.TS_OLD_SURVEY_NO] = "true";
    target[SURVEY_HIERARCHY_META.TS_OLD_TS_NO] = "";
  } else if (variant === "ts_old_ts_no") {
    target[SURVEY_HIERARCHY_META.TS_OLD_TS_NO] = "true";
    target[SURVEY_HIERARCHY_META.TS_OLD_SURVEY_NO] = "";
  }
}

/** Resolve hierarchy save tag (handles TS No + Old Survey No → old_ts_survey_no column). */
export function resolveSurveyHierarchySaveTag(
  field: ChecklistFieldDef,
  row: Record<string, string>,
  fields: ChecklistFieldDef[]
): string {
  const tag = resolveSurveyMappedFieldTag(field);
  if (!tag) return "";
  const surveyType = normalizeSurveyTypeValue(resolveSurveyTypeFromHierarchyRow(fields, row));
  if (surveyType !== "ts_no") return tag;
  if (tag !== "gdSurvey_old_survey_no") return tag;
  const variant = resolveTsOldSurveyNumberVariant(row);
  if (variant === "ts_old_survey_no") return "gdSurvey_old_ts_s_no";
  return tag;
}

/** General Detail row keys → survey type bucket (legacy tags and raw row keys). */
const SURVEY_ROW_KEY_TO_TYPE_BUCKET: Record<string, string> = {
  old_survey_no: "survey_no",
  survey_no: "survey_no",
  old_natham_survey_no: "natham_survey_no",
  natham_survey_no: "natham_survey_no",
  old_ts_no: "ts_no",
  ts_no: "ts_no",
  old_ts_s_no: "ts_no",
};

const ALL_SURVEY_TYPE_CONDITIONAL_TAGS = new Set(
  Object.values(SURVEY_TYPE_CONDITIONAL_TAGS).flat()
);

export function normalizeSurveyTypeValue(raw: string | undefined | null): string {
  const v = String(raw ?? "").trim();
  if (!v) return "";
  const lower = v.toLowerCase();
  if (lower === "natham_survey_no" || lower === "natham survey no" || lower === "natham survey") {
    return "natham_survey_no";
  }
  if (
    lower === "ts_no" ||
    lower === "ts no" ||
    lower === "t.s.no" ||
    lower === "t.s. no" ||
    lower === "t.s no"
  ) {
    return "ts_no";
  }
  if (lower === "survey_no" || lower === "survey no" || lower === "s.no") {
    return "survey_no";
  }
  if (lower.includes("natham")) return "natham_survey_no";
  if ((lower.includes("t.s") || lower.includes("ts")) && lower.includes("no")) return "ts_no";
  if (lower.includes("survey") && lower.includes("no")) return "survey_no";
  return lower.replace(/\s+/g, "_");
}

export function isSurveyTypeConditionalFieldTag(tag: string): boolean {
  return ALL_SURVEY_TYPE_CONDITIONAL_TAGS.has(normalizeSurveyTagId(tag));
}

export function surveyTypeBucketForFieldTag(tag: string): string | null {
  const normalized = normalizeSurveyTagId(tag);
  for (const [surveyType, tags] of Object.entries(SURVEY_TYPE_CONDITIONAL_TAGS)) {
    if (tags.includes(normalized)) return surveyType;
  }
  const rowKey = resolveSurveyTagRowKey(normalized);
  if (rowKey && SURVEY_ROW_KEY_TO_TYPE_BUCKET[rowKey]) {
    return SURVEY_ROW_KEY_TO_TYPE_BUCKET[rowKey];
  }
  return null;
}

function surveyTypeBucketForField(field: ChecklistFieldDef): string | null {
  if (shouldSkipSurveyTypeConditionalInference(field)) return null;
  const tag = resolveSurveyMappedFieldTag(field);
  if (tag) {
    const bucket = surveyTypeBucketForFieldTag(tag);
    if (bucket) return bucket;
  }
  const norm = String(field.label ?? "").toLowerCase();
  if (norm.includes("natham")) return "natham_survey_no";
  if ((norm.includes("t.s") || /\bts\b/.test(norm)) && !norm.includes("natham")) return "ts_no";
  if (norm.includes("survey") && (norm.includes("no") || norm.includes("number"))) {
    if (norm.includes("natham")) return "natham_survey_no";
    if (norm.includes("t.s") || /\bts\b/.test(norm)) return "ts_no";
    return "survey_no";
  }
  return null;
}

export function isSurveyTypeConditionalField(field: ChecklistFieldDef): boolean {
  const bucket = surveyTypeBucketForField(field);
  if (bucket) return true;
  const tag = resolveSurveyMappedFieldTag(field);
  return tag ? isSurveyTypeConditionalFieldTag(tag) : false;
}

export type SurveyTypeFieldSource = {
  fields: ChecklistFieldDef[];
  readValue: (fieldId: string) => string;
};

export function resolveSurveyTypeFromHierarchyRow(
  fields: ChecklistFieldDef[],
  row: Record<string, string>,
  fallbackSurveyType?: string | null,
  readValue: (fieldId: string) => string = (id) => row[id] ?? "",
  extraSources: SurveyTypeFieldSource[] = []
): string {
  const sources: SurveyTypeFieldSource[] = [{ fields, readValue }, ...extraSources];
  for (const src of sources) {
    for (const f of src.fields) {
      if (resolveSurveyMappedFieldTag(f) === "gdSurvey_survey_type") {
        const fromField = normalizeSurveyTypeValue(src.readValue(f.id));
        if (fromField) return fromField;
      }
    }
  }
  const fromRowKey = normalizeSurveyTypeValue(row.survey_type);
  if (fromRowKey) return fromRowKey;
  const fromGdTag = normalizeSurveyTypeValue(
    mapSurveyTagToSourceRow("gdSurvey_survey_type", row)
  );
  if (fromGdTag) return fromGdTag;
  return normalizeSurveyTypeValue(fallbackSurveyType);
}

export function isSurveyFieldVisibleForSurveyType(
  field: ChecklistFieldDef,
  surveyType: string,
  row?: Record<string, string>
): boolean {
  if (!isSurveyTypeConditionalField(field)) return true;
  if (!surveyType) return false;
  const tag = resolveSurveyMappedFieldTag(field);
  const bucket = surveyTypeBucketForField(field);
  const normalizedType = normalizeSurveyTypeValue(surveyType);

  if (normalizedType === "ts_no" && row) {
    const variant = resolveTsOldSurveyNumberVariant(row);
    if (TS_OLD_SURVEY_NUMBER_TAGS.has(tag)) {
      return variant === "ts_old_survey_no" || hasTsOldSurveyNumberValue(row);
    }
    if (TS_OLD_TS_NUMBER_TAGS.has(tag)) {
      if (variant === "ts_old_survey_no" || hasTsOldSurveyNumberValue(row)) return false;
      return variant === "ts_old_ts_no" || hasTsOldTsNumberValue(row) || variant === null;
    }
    if (bucket === "survey_no" && tag === "gdSurvey_old_survey_no") {
      return variant === "ts_old_survey_no" || hasTsOldSurveyNumberValue(row);
    }
  }

  if (!bucket) return false;
  if (bucket !== normalizedType) {
    if (
      normalizedType === "ts_no" &&
      bucket === "survey_no" &&
      tag === "gdSurvey_old_survey_no" &&
      row &&
      resolveTsOldSurveyNumberVariant(row) === "ts_old_survey_no"
    ) {
      return true;
    }
    return false;
  }
  return true;
}

function parseMeasurementListFromRow(
  rawJson: string,
  rawFlat: string,
  keepEmpty = false
): string[] {
  if (rawJson.startsWith("[")) {
    try {
      const parsed = JSON.parse(rawJson);
      if (Array.isArray(parsed)) {
        const mapped = parsed.map((v) => strVal(v));
        return keepEmpty ? mapped : mapped.filter((v) => v);
      }
    } catch {
      // fallback to flat
    }
  }
  if (!rawFlat) return [];
  const mapped = rawFlat.split(",").map((v) => strVal(v));
  return keepEmpty ? mapped : mapped.filter((v) => v);
}

/** Read custom measurement label/value arrays from a hierarchy row or document source. */
export function parseCustomMeasurementLists(
  row: Record<string, string>
): { labels: string[]; values: string[] } {
  const labels = parseMeasurementListFromRow(
    strVal(row[MEAS_DIR_CUSTOM_LABELS_JSON_KEY]),
    strVal(row[MEAS_DIR_CUSTOM_LABELS_KEY] ?? row.measure_custom),
    true
  );
  const values = parseMeasurementListFromRow(
    strVal(row[MEAS_DIR_CUSTOM_VALUES_JSON_KEY]),
    strVal(row[MEAS_DIR_CUSTOM_VALUES_KEY] ?? row[`${MEAS_DIR_BY_LABEL_PREFIX}custom`]),
    true
  );
  const count = Math.max(labels.length, values.length);
  return {
    labels: Array.from({ length: count }, (_, i) => labels[i] ?? ""),
    values: Array.from({ length: count }, (_, i) => values[i] ?? ""),
  };
}

function rowHasSavedCustomMeasurement(
  row: Record<string, string>,
  fieldId: string
): boolean {
  if (strVal(row[fieldId])) return true;
  const { labels, values } = parseCustomMeasurementLists(row);
  return labels.some((l) => l.trim() !== "") || values.some((v) => v.trim() !== "");
}

/**
 * Merge saved custom measurement entries with document data.
 * Preserves saved values; appends document-only entries at the end.
 */
export function mergeCustomMeasurementPrefill(
  row: Record<string, string>,
  field: ChecklistFieldDef,
  source: Record<string, string>,
  fallbackSource?: Record<string, string>
): boolean {
  const saved = parseCustomMeasurementLists(row);
  const doc = parseCustomMeasurementLists(source);
  const fallback = fallbackSource ? parseCustomMeasurementLists(fallbackSource) : { labels: [], values: [] };
  const docLabels = doc.labels.some((l) => l.trim()) ? doc.labels : fallback.labels;
  const docValues = doc.values.some((v) => v.trim()) ? doc.values : fallback.values;
  const savedLen = Math.max(
    saved.labels.filter((l) => l.trim()).length,
    saved.values.filter((v) => v.trim()).length,
    strVal(row[field.id]) ? 1 : 0
  );
  const docLen = Math.max(docLabels.length, docValues.length);
  const count = Math.max(savedLen, docLen);
  if (count === 0) return false;

  const labels: string[] = [];
  const values: string[] = [];
  for (let i = 0; i < count; i++) {
    const savedLabel = strVal(saved.labels[i]);
    const savedVal =
      i === 0 && !strVal(saved.values[i]) ? strVal(row[field.id]) : strVal(saved.values[i]);
    labels.push(savedLabel || docLabels[i] || "Custom");
    values.push(savedVal || docValues[i] || "");
  }

  row[MEAS_DIR_CUSTOM_LABELS_JSON_KEY] = JSON.stringify(labels);
  row[MEAS_DIR_CUSTOM_VALUES_JSON_KEY] = JSON.stringify(values);
  row[MEAS_DIR_CUSTOM_LABELS_KEY] = labels.filter((l) => l.trim()).join(", ");
  row[MEAS_DIR_CUSTOM_VALUES_KEY] = values.filter((v) => v.trim()).join(", ");
  if (labels[0]) {
    row[`${MEAS_DIR_CUSTOM_LABEL_BY_FIELD_PREFIX}${field.id}`] = labels[0];
    row.measure_custom = labels.filter((l) => l.trim()).join(", ");
  }
  if (values[0]) {
    row[field.id] = normalizePrefillValueForField(field, values[0]);
  }
  return true;
}

function prefillHierarchyField(
  row: Record<string, string>,
  field: ChecklistFieldDef,
  source: Record<string, string>,
  fallbackSource?: Record<string, string>,
  force?: boolean
): void {
  const isCustomField = normalizeDirectionLabel(field.label) === "custom";

  if (isCustomField) {
    if (!force && rowHasSavedCustomMeasurement(row, field.id)) {
      mergeCustomMeasurementPrefill(row, field, source, fallbackSource);
      return;
    }
    const labels =
      parseMeasurementListFromRow(
        strVal(source[MEAS_DIR_CUSTOM_LABELS_JSON_KEY]),
        strVal(source[MEAS_DIR_CUSTOM_LABELS_KEY] ?? source.measure_custom)
      ) || [];
    const values =
      parseMeasurementListFromRow(
        strVal(source[MEAS_DIR_CUSTOM_VALUES_JSON_KEY]),
        strVal(source[MEAS_DIR_CUSTOM_VALUES_KEY] ?? source[`${MEAS_DIR_BY_LABEL_PREFIX}custom`])
      ) || [];
    const fallbackLabels = parseMeasurementListFromRow(
      strVal(fallbackSource?.[MEAS_DIR_CUSTOM_LABELS_JSON_KEY]),
      strVal(fallbackSource?.[MEAS_DIR_CUSTOM_LABELS_KEY] ?? fallbackSource?.measure_custom)
    );
    const fallbackValues = parseMeasurementListFromRow(
      strVal(fallbackSource?.[MEAS_DIR_CUSTOM_VALUES_JSON_KEY]),
      strVal(
        fallbackSource?.[MEAS_DIR_CUSTOM_VALUES_KEY] ??
          fallbackSource?.[`${MEAS_DIR_BY_LABEL_PREFIX}custom`]
      )
    );
    const finalLabels = labels.length ? labels : fallbackLabels;
    const finalValues = values.length ? values : fallbackValues;
    if (finalLabels.length) {
      row[MEAS_DIR_CUSTOM_LABELS_JSON_KEY] = JSON.stringify(finalLabels);
      row[`${MEAS_DIR_CUSTOM_LABEL_BY_FIELD_PREFIX}${field.id}`] = finalLabels[0];
    }
    if (finalValues.length) {
      row[MEAS_DIR_CUSTOM_VALUES_JSON_KEY] = JSON.stringify(finalValues);
      row[field.id] = normalizePrefillValueForField(field, finalValues[0]);
      return;
    }
  }

  const existing = strVal(row[field.id]);
  // Without force, saved / user-entered hierarchy values win over GD prefill.
  // With force (reload after General Detail edits), GD is the source of truth.
  if (!force && !hierarchyPrefillFieldUnset(field, existing)) return;

  const tag = resolveSurveyHierarchyFieldTag(field);
  if (!tag) return;
  let val = mapTagToSourceRow(tag, source);
  if (!val) val = valueForMeasurementDirectionField(field, source);
  if (!val && fallbackSource) {
    val = mapTagToSourceRow(tag, fallbackSource);
    if (!val) val = valueForMeasurementDirectionField(field, fallbackSource);
  }
  if (force || val !== "") {
    row[field.id] = normalizePrefillValueForField(field, val);
  }

  // Runtime UX: when checklist field label is "Custom", show typed
  // `measure_custom` text as field label.
  if (isCustomField) {
    let customLabel = "";
    const jsonLabels = strVal(source[MEAS_DIR_CUSTOM_LABELS_JSON_KEY]);
    if (jsonLabels.startsWith("[")) {
      try {
        const parsed = JSON.parse(jsonLabels);
        if (Array.isArray(parsed) && parsed.length > 0) {
          customLabel = strVal(parsed[0]);
        }
      } catch {
        // fall through to flat keys
      }
    }
    if (!customLabel) {
      customLabel =
        strVal(source[MEAS_DIR_CUSTOM_LABELS_KEY] ?? source.measure_custom) ||
        strVal(fallbackSource?.[MEAS_DIR_CUSTOM_LABELS_KEY] ?? fallbackSource?.measure_custom);
    }
    if (customLabel) {
      row[`${MEAS_DIR_CUSTOM_LABEL_BY_FIELD_PREFIX}${field.id}`] = customLabel;
    }
  }
}

function emptyHierarchyRow(fields: ChecklistFieldDef[]): Record<string, string> {
  const row: Record<string, string> = {};
  fields.forEach((f) => {
    row[f.id] = "";
  });
  return row;
}

/** Apply General Detail hierarchy prefill for survey verification repeatable groups. */
export function applySurveyHierarchyPrefill(
  section: ChecklistSectionDef,
  repeatableValues: Record<string, Record<string, string>[]>,
  flatRows: ReturnType<typeof flattenSurveyHierarchyRows>,
  options?: {
    titleDeedRow?: Record<string, string> | null;
    forceFields?: boolean;
  }
): void {
  const titleDeedRow = options?.titleDeedRow ?? null;
  const forceFields = options?.forceFields === true;
  surveyHierarchyLinkedGroups(section).forEach((group) => {
    const level = group.surveyHierarchyLevel;
    if (!level) return;
    let sourceRows = flatRows[level];
    if (group.naturePropertyKey) {
      sourceRows = sourceRows.filter(
        (r) => String(r[SURVEY_HIERARCHY_META.NATURE_KEY] ?? "") === group.naturePropertyKey
      );
    }
    if (!sourceRows.length) return;

    const key = repeatableEntriesKey(section.id, group.id);
    const existing = repeatableValues[key] || [];
    const existingById = new Map<string, Record<string, string>>();
    existing.forEach((r) => {
      const id = hierarchyRowIdentity(level, r);
      if (id) existingById.set(id, r);
    });

    const rows: Record<string, string>[] = [];
    const usedExistingIndices = new Set<number>();
    sourceRows.forEach((source, idx) => {
      const id = hierarchyRowIdentity(level, source);
      let base = emptyHierarchyRow(group.fields);
      const matchedById = id ? existingById.get(id) : undefined;
      if (matchedById) {
        base = { ...matchedById };
        existing.forEach((r, i) => {
          if (r === matchedById) usedExistingIndices.add(i);
        });
      } else if (existing[idx] && typeof existing[idx] === "object" && !usedExistingIndices.has(idx)) {
        base = { ...existing[idx] };
        usedExistingIndices.add(idx);
      } else {
        const unusedIdx = existing.findIndex((_, i) => !usedExistingIndices.has(i));
        if (unusedIdx >= 0) {
          base = { ...existing[unusedIdx] };
          usedExistingIndices.add(unusedIdx);
        } else {
          base = emptyHierarchyRow(group.fields);
        }
      }
      Object.keys(SURVEY_HIERARCHY_META).forEach((mk) => {
        const metaKey = SURVEY_HIERARCHY_META[mk as keyof typeof SURVEY_HIERARCHY_META];
        if (source[metaKey]) base[metaKey] = source[metaKey];
      });
      if (level === "survey") {
        const surveyType = strVal(source.survey_type);
        if (surveyType) base.survey_type = surveyType;
        const tsVisibilityKeys = [
          "ts_old_survey_no",
          "ts_old_ts_no",
          "old_ts_s_no",
          "old_ts_survey_no",
          "old_ts_no",
        ] as const;
        tsVisibilityKeys.forEach((k) => {
          const v = strVal(source[k]);
          if (v) base[k] = v;
        });
        copyTsOldSurveyNumberMeta(base, source);
      }
      const surIdFromRow = strVal(base[SURVEY_HIERARCHY_META.SUR_ID]);
      const prefillSource = buildHierarchyPrefillSource(level, source, flatRows, {
        ...(surIdFromRow ? { surId: surIdFromRow } : {}),
      });
      group.fields.forEach((f) =>
        prefillHierarchyField(base, f, prefillSource, titleDeedRow ?? undefined, forceFields)
      );
      rows.push(base);
    });

    repeatableValues[key] = rows;
  });
}

export function getGeneralDetailsForDocument(
  scrutinyApiData: any,
  fdId: string | number | null | undefined
): GeneralDetailsDocumentHierarchy | null {
  const fd = resolveSurveyDocumentFdId(fdId, scrutinyApiData);
  if (!fd || !scrutinyApiData?.generalDetailsByDocument) return null;
  const block = scrutinyApiData.generalDetailsByDocument[fd];
  if (!block || typeof block !== "object") return null;
  return block as GeneralDetailsDocumentHierarchy;
}

/** Map selected document id (`base_id` / `fd_id`) to scrutiny general-detail key. */
export function resolveSurveyDocumentFdId(
  activeDocumentFdId: string | number | null | undefined,
  scrutinyApiData: any
): string {
  const active = strVal(activeDocumentFdId);
  const byDoc = scrutinyApiData?.generalDetailsByDocument;
  const hasSchedules = (fdKey: string): boolean => {
    const block = byDoc?.[fdKey];
    return Array.isArray(block?.schedules) && block.schedules.length > 0;
  };

  if (active && byDoc && typeof byDoc === "object") {
    if (hasSchedules(active)) return active;
    if (byDoc[active]) return active;
  }

  const surveyDocs = Array.isArray(scrutinyApiData?.surveyDocuments)
    ? scrutinyApiData.surveyDocuments
    : [];
  if (active && surveyDocs.some((d: any) => strVal(d?.fd_id) === active)) return active;

  const titleDeed = scrutinyApiData?.surveyNoVerification?.titleDeed;
  if (Array.isArray(titleDeed) && active) {
    const match = titleDeed.find(
      (r: any) => strVal(r?.fd_id) === active || strVal(r?.base_id) === active
    );
    if (match) return strVal(match.fd_id ?? match.base_id) || active;
  }

  if (byDoc && typeof byDoc === "object") {
    const withSchedules = Object.keys(byDoc).find((fdKey) => hasSchedules(fdKey));
    if (withSchedules) return withSchedules;
    if (active && byDoc[active]) return active;
    const firstKey = Object.keys(byDoc)[0];
    if (firstKey) return firstKey;
  }

  return active;
}

/** Prefer a document fd_id that has General Detail schedules in the API payload. */
export function resolveSurveyDocumentFdWithSchedules(
  activeDocumentFdId: string | number | null | undefined,
  scrutinyApiData: any
): string {
  const resolved = resolveSurveyDocumentFdId(activeDocumentFdId, scrutinyApiData);
  const byDoc = scrutinyApiData?.generalDetailsByDocument;
  if (
    resolved &&
    byDoc &&
    Array.isArray(byDoc[resolved]?.schedules) &&
    byDoc[resolved].schedules.length > 0
  ) {
    return resolved;
  }
  if (byDoc && typeof byDoc === "object") {
    const match = Object.keys(byDoc).find(
      (fdKey) => Array.isArray(byDoc[fdKey]?.schedules) && byDoc[fdKey].schedules.length > 0
    );
    if (match) return match;
  }
  return resolved;
}

/** All General Detail document keys that have at least one schedule (file-level survey verification). */
export function listSurveyDocumentFdIdsWithSchedules(
  generalDetailsByDocument?: Record<string, GeneralDetailsDocumentHierarchy> | null
): string[] {
  if (!generalDetailsByDocument || typeof generalDetailsByDocument !== "object") return [];
  return Object.keys(generalDetailsByDocument).filter((fdKey) => {
    const schedules = generalDetailsByDocument[fdKey]?.schedules;
    return Array.isArray(schedules) && schedules.length > 0;
  });
}
